
import { mixins } from "vue-class-component";
import { DateUtilsMixin } from "@/mixins/date-utils-mixin";
import { Getter, State } from 'vuex-class';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { IdLookup } from '@/store/validations/types';
import { Recipient } from '@/store/recipients/types';
import { SaveableSection, SaveProvider, SaveResult, TableConfig } from '@/types';
import { RecipientJourney } from '@/store/recipientJourney/types';
import { AlcoholicLiverDiseaseProgram } from '@/store/organSpecificDetails/types';
import { AldProgramForm } from '@/components/organs/liver/AldProgramParticipationDetails.vue';
import TextInput from '@/components/shared/TextInput.vue';
import DateInput from '@/components/shared/DateInput.vue';
import SubSection from '@/components/shared/SubSection.vue';
import SelectInput from "@/components/shared/SelectInput.vue";
import { GenericCodeValue } from '@/store/types';
import { ALDStatusListCodeValue } from '@/store/lookups/types';

export interface AldProgramRow {
  _id?: string;
  statusDate?: string;
  journeyPhase?: string;
  aldStatus?: string;
  journeyPhaseDecision?: string;
  enteredBy?: string | null;
}

export interface AldProgramPageState {
  _id?: string;
  statusDate?: string;
  journeyPhase?: number;
  aldStatus?: number;
  journeyPhaseDecision?: string;
}

@Component({
  components: {
    SubSection,
    TextInput,
    DateInput,
    SelectInput
  }
})
export default class AldProgram extends mixins(DateUtilsMixin) implements SaveableSection {
  @State(state => state.recipients.selectedRecipient) recipient!: Recipient;
  @State(state => state.journeyState.selectedJourney) journey!: RecipientJourney;
  @State(state => state.pageState.currentPage.aldProgramDetails) editState!: AldProgramForm;
  @State(state => state.journeyState.selectedAldProgram) selectedAldProgram!: AlcoholicLiverDiseaseProgram;
  @State(state => state.organSpecificDetails.aldPrograms) private aldPrograms!: AlcoholicLiverDiseaseProgram[];

  // Props
  @Prop({ default: false }) newJourney!: boolean;
  @Prop({ default: false }) canSave!: boolean;

  // Getters
  @Getter('clientId', { namespace: 'recipients' }) recipientId!: string;
  @Getter('journeyId', { namespace: 'journeyState' }) journeyId!: string|undefined;
  @Getter('canSaveGetter', { namespace: 'validations' }) private canSaveGetter!: (newRecord: boolean) => boolean;
  @Getter('isLastEntry', { namespace: 'utilities' }) private isLastEntry!: (id: string, entries: any[]) => boolean;
  @Getter("aldStatusOptions", { namespace: "lookups" }) private aldStatusOptions: any;
  @Getter("journeyPhaseOptions", { namespace: "lookups" }) private journeyPhaseOptions: any;
  @Getter('lookupValue', { namespace: 'lookups' }) lookupValue!: (code: string|undefined, lookupId: string) => any;

  public lookupsToLoad = [
    "liver_alcoholic_liver_disease_statuses",
    "liver_alcoholic_liver_disease_journey_phases",
  ];

  get aldStatusListOptions(): any{
    // show No Longer Participating in dropdown only if there's any ALD Program already created.
    const result = this.aldStatusOptions.filter((option: GenericCodeValue) => {
      return this.aldPrograms?.length != 0 || option.code != ALDStatusListCodeValue.NoLongerParticipating;
    });
    
    return result;
  }

  /**
   * Gets the table configuration for Alcoholic Liver Disease Program.
   * 
   * @return {TableConfig} Alcoholic Liver Disease Program table configration
   */
  get aldTableConfig(): TableConfig {
    return {
      data: this.aldRows || [],
      columns: [
        { label: this.$t('ald_status_date').toString(), field: 'statusDate' },
        { label: this.$t('journey_phase').toString(), field: 'journeyPhase' },
        { label: this.$t('ald_status').toString(), field: 'aldStatus' },
        { label: this.$t('journey_phase_decision').toString(), field: 'journeyPhaseDecision' },
        { label: this.$t('last_updated_by_user').toString(), field: 'enteredBy' },
      ],
      empty: this.$t('use_form_below').toString(),
      createButton: this.canEdit,
      createText: this.$t('create_ald').toString(),
      pagination: true,
    };
  }

  /**
   * Return formatted rows for the table
   * 
   * @return {AldProgramRow[]} All formatted rows from ald_program_participations
   */
  get aldRows(): AldProgramRow[] {
    if (!this.aldPrograms) return [];

    const rows = this.aldPrograms.map((aldProgram: AlcoholicLiverDiseaseProgram) => {
      const row: AldProgramRow = {
        _id: aldProgram._id ? aldProgram._id : undefined,
        statusDate: aldProgram.ald_status_date ? this.parseDisplayDateUi(aldProgram.ald_status_date) : '-',
        journeyPhase: this.lookupValue(aldProgram.journey_phase_code?.toString(), 'liver_alcoholic_liver_disease_journey_phases') || '-',
        aldStatus: this.lookupValue(aldProgram.ald_status_code?.toString(), 'liver_alcoholic_liver_disease_statuses') || '-',
        journeyPhaseDecision: this.$t(aldProgram.journey_phase_decision || '').toString() || '-',
        enteredBy: aldProgram.updated_by || aldProgram.created_by,
      };
      return row;
    });
    return rows;
  }

  /**
   * Return true if we can edit the selected entry
   *
   * @returns {boolean} true if we can edit
   */
  get canEdit(): boolean {
    if (this.newJourney) return false;
    if (this.journey.completed) return false;
    
    return true;
  }

  /**
   * Called when the component mounts
   */
  public mounted(): void {
    this.initializeForm();
  }

  // PRIVATE

  /**
   * Initialize the pageState
   */
  private initializeForm(): void {
    this.$store.dispatch('organSpecificDetails/loadAlcoholicLiverDiseaseProgram',{ recipientId: this.recipientId, journeyId: this.journeyId }).then(() => {
      // Reset empty pageState
      this.$store.commit('pageState/set', {
        pageKey: 'aldProgramDetails',
        componentKey: 'aldProgram',
        value: this.buildAldPageState()
      });
    });
  }

  /**
   * Builds the AldProgramPageState 
   * 
   * @param {AlcoholicLiverDiseaseProgram} aldProgram Alcoholic Liver Disease Program entry
   * @return {AldProgramPageState} The pageState 
   */
  private buildAldPageState(aldProgram?: AlcoholicLiverDiseaseProgram): AldProgramPageState {
    // Clear any errors
    this.$emit('clear');
    const lastAldjourneyPhase = this.aldPrograms ? this.aldPrograms[0] : null;
    const defaultState: AldProgramPageState = {
      statusDate: this.currentDateUi(),
      journeyPhase: undefined,
      aldStatus: undefined,
    };

    // Return a default pageState if we don't have aldProgram
    if (!aldProgram) return defaultState;

    const result: AldProgramPageState = {
      _id: aldProgram._id,
      statusDate: aldProgram.ald_status_date ? this.parseDateUi(aldProgram.ald_status_date) : defaultState.statusDate,
      journeyPhase: aldProgram.journey_phase_code ? aldProgram.journey_phase_code : defaultState.journeyPhase,
      aldStatus: aldProgram.ald_status_code ? aldProgram.ald_status_code : defaultState.aldStatus,
      journeyPhaseDecision: this.$t(aldProgram.journey_phase_decision || '').toString() || '-',
    };
    return result;
  }

  /**
   * Resets to an empty AldProgramPageState and clear any selected item
   */
  private createAldProgram(): void {
    // Clear any selected decision
    this.$store.commit('journeyState/clearAldProgram');
    // Build empty page state
    const aldProgramPageState: AldProgramPageState = this.buildAldPageState();
    Vue.set(this.editState, 'aldProgram', aldProgramPageState);
  }

  /**
   * Build AldProgramPageState from the selected row 
   */
  private selectAldProgram(event: any): void {
    const selectedId = event.row._id || undefined;

    // Return if we don't have what we need
    if (!selectedId || !this.editState || !this.aldPrograms) return;

    // Find the item matching our selection
    const aldProgram = this.aldPrograms.find((aldProgram: AlcoholicLiverDiseaseProgram) => {
      return aldProgram?._id === selectedId;
    });

    // Return if we didn't find anything
    if (!aldProgram) return;

    // Store the entry
    this.$store.commit('journeyState/selectAldProgram', aldProgram);
    // Build aldPageState with the entry
    const aldProgramPageState: AldProgramPageState = this.buildAldPageState(aldProgram);
    Vue.set(this.editState, 'aldProgram', aldProgramPageState);
  }

  /**
   * Generic extract 
   */
  public extractPatch(): AlcoholicLiverDiseaseProgram {
    if (!this.editState || !this.editState.aldProgram) return {};
    return this.extractAldProgramPatch(this.editState.aldProgram);
  }

  /**
   * Extract from editState our alcoholic_liver_disease_program patch 
   */
  private extractAldProgramPatch(aldProgram: AldProgramPageState): AlcoholicLiverDiseaseProgram {
    if (!aldProgram) return {};
    return {
      _id: aldProgram._id ? aldProgram._id : undefined,
      ald_status_date: this.sanitizeDateApi(aldProgram.statusDate) || null,
      journey_phase_code: aldProgram.journeyPhase || null,
      ald_status_code: aldProgram.aldStatus || null,
    };
  }

  /**
   * Clears all save notifications shown by the form.
   *
   * Gets the Save Provider associated with the form, and requests that it reset its own Save Toolbar
   */
   public resetSaveToolbar(): void {
    // Refer to the save provider that handle the areas present on this form component
    const saveProvider = this.$refs.aldProgram as unknown as SaveProvider;
    // Reset the save provider's save toolbar
    saveProvider.resetSaveToolbar();
  }

  /**
   * Attempt to save the entry 
   */
  public savePatch(): void {
    // Refer to the save provider that handles this form area
    const saveProvider = this.$refs.aldProgram as unknown as SaveProvider;
    // Generate payload based on current editState
    const aldProgramPayload = {
      recipientId: this.recipientId,
      journeyId: this.journeyId,
      aldProgram: this.extractPatch(),
    };
    // Dispatch save action and register the response
    this.$store.dispatch('organSpecificDetails/saveAlcoholicLiverDiseaseProgram', aldProgramPayload).then((success: SaveResult) => {
      // If successful then reload recipient to get updated journey details
      this.$store.dispatch('recipients/get', this.recipientId).then(() => {
        this.$store.dispatch('journeyState/getJourney', this.journeyId).then(() => {
          this.$store.dispatch('organSpecificDetails/loadAlcoholicLiverDiseaseProgram',{ recipientId: this.recipientId, journeyId: this.journeyId });
          // Re-initialize
          this.initializeForm();
          // Register save result
          saveProvider.registerSaveResult(success);
        });
      });
      // Register success notification
      saveProvider.registerSaveResult(success);
    }).catch((error: SaveResult) => {
      // Emit event to handle errors
      this.$emit('handleErrors', error);
      // Show error notification
      saveProvider.registerSaveResult(error);
    });
  }

    // API response keys on the left, id for our UI on the right
  public idLookup: IdLookup = {
    'ald_status_date' : 'ald_status_date',
    'journey_phase_code' : 'journey_phase',
    'ald_status_code' : 'ald_status'
  };
}
